import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface Challenge {
  id: string;
  title: string;
  description: string;
  xpReward: number;
  challengeType: 'daily' | 'weekly';
  actionType: string;
  actionTarget: string | null;
  targetCount: number;
  icon: string;
}

export interface ChallengeProgress {
  id: string;
  challengeId: string;
  currentProgress: number;
  isCompleted: boolean;
  completedAt: string | null;
  assignedDate: string;
  expiresAt: string;
  xpClaimed: boolean;
  challenge: Challenge;
}

export const useChallenges = () => {
  const { user } = useAuth();
  const [dailyChallenges, setDailyChallenges] = useState<ChallengeProgress[]>([]);
  const [weeklyChallenges, setWeeklyChallenges] = useState<ChallengeProgress[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchChallenges = useCallback(async () => {
    if (!user) {
      setDailyChallenges([]);
      setWeeklyChallenges([]);
      setLoading(false);
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch available challenges
      const { data: challenges, error: challengesError } = await supabase
        .from('daily_challenges')
        .select('*')
        .eq('is_active', true);

      if (challengesError) throw challengesError;

      // Fetch user's progress
      const { data: progress, error: progressError } = await supabase
        .from('user_challenge_progress')
        .select('*')
        .eq('user_id', user.id)
        .gte('expires_at', new Date().toISOString());

      if (progressError) throw progressError;

      // Map challenges to progress, creating new progress entries if needed
      const dailyWithProgress: ChallengeProgress[] = [];
      const weeklyWithProgress: ChallengeProgress[] = [];

      for (const challenge of challenges || []) {
        const existingProgress = progress?.find(p => p.challenge_id === challenge.id);
        
        const challengeProgress: ChallengeProgress = {
          id: existingProgress?.id || '',
          challengeId: challenge.id,
          currentProgress: existingProgress?.current_progress || 0,
          isCompleted: existingProgress?.is_completed || false,
          completedAt: existingProgress?.completed_at || null,
          assignedDate: existingProgress?.assigned_date || today,
          expiresAt: existingProgress?.expires_at || getExpiresAt(challenge.challenge_type),
          xpClaimed: existingProgress?.xp_claimed || false,
          challenge: {
            id: challenge.id,
            title: challenge.title,
            description: challenge.description,
            xpReward: challenge.xp_reward,
            challengeType: challenge.challenge_type as 'daily' | 'weekly',
            actionType: challenge.action_type,
            actionTarget: challenge.action_target,
            targetCount: challenge.target_count || 1,
            icon: challenge.icon || 'target',
          },
        };

        if (challenge.challenge_type === 'daily') {
          dailyWithProgress.push(challengeProgress);
        } else {
          weeklyWithProgress.push(challengeProgress);
        }
      }

      setDailyChallenges(dailyWithProgress);
      setWeeklyChallenges(weeklyWithProgress);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getExpiresAt = (type: string): string => {
    const now = new Date();
    if (type === 'daily') {
      now.setHours(23, 59, 59, 999);
    } else {
      // End of the week (Sunday)
      const daysUntilSunday = 7 - now.getDay();
      now.setDate(now.getDate() + daysUntilSunday);
      now.setHours(23, 59, 59, 999);
    }
    return now.toISOString();
  };

  const updateProgress = useCallback(async (
    actionType: string,
    increment: number = 1,
    actionTarget?: string
  ) => {
    if (!user) return;

    try {
      // Find matching challenges
      const allChallenges = [...dailyChallenges, ...weeklyChallenges];
      const matchingChallenges = allChallenges.filter(cp => 
        cp.challenge.actionType === actionType &&
        (!cp.challenge.actionTarget || cp.challenge.actionTarget === actionTarget) &&
        !cp.isCompleted
      );

      for (const challenge of matchingChallenges) {
        const newProgress = Math.min(
          challenge.currentProgress + increment,
          challenge.challenge.targetCount
        );
        const isNowCompleted = newProgress >= challenge.challenge.targetCount;

        if (challenge.id) {
          // Update existing progress
          await supabase
            .from('user_challenge_progress')
            .update({
              current_progress: newProgress,
              is_completed: isNowCompleted,
              completed_at: isNowCompleted ? new Date().toISOString() : null,
            })
            .eq('id', challenge.id);
        } else {
          // Create new progress entry
          await supabase
            .from('user_challenge_progress')
            .insert({
              user_id: user.id,
              challenge_id: challenge.challengeId,
              current_progress: newProgress,
              is_completed: isNowCompleted,
              completed_at: isNowCompleted ? new Date().toISOString() : null,
              assigned_date: new Date().toISOString().split('T')[0],
              expires_at: getExpiresAt(challenge.challenge.challengeType),
            });
        }

        if (isNowCompleted) {
          toast.success(`🎯 Challenge Complete: ${challenge.challenge.title}! +${challenge.challenge.xpReward} XP`);
        }
      }

      // Refresh challenges
      fetchChallenges();
    } catch (error) {
      console.error('Error updating challenge progress:', error);
    }
  }, [user, dailyChallenges, weeklyChallenges, fetchChallenges]);

  const claimXP = useCallback(async (progressId: string): Promise<number> => {
    if (!user || !progressId) return 0;

    try {
      const challenge = [...dailyChallenges, ...weeklyChallenges].find(c => c.id === progressId);
      if (!challenge || !challenge.isCompleted || challenge.xpClaimed) {
        return 0;
      }

      // Mark XP as claimed
      await supabase
        .from('user_challenge_progress')
        .update({ xp_claimed: true })
        .eq('id', progressId);

      // Add XP to user's streak
      const { data: streakData } = await supabase
        .from('user_streaks')
        .select('total_xp, level')
        .eq('user_id', user.id)
        .single();

      const newXP = (streakData?.total_xp || 0) + challenge.challenge.xpReward;
      const newLevel = Math.floor(newXP / 100) + 1;

      await supabase
        .from('user_streaks')
        .update({ 
          total_xp: newXP,
          level: newLevel,
        })
        .eq('user_id', user.id);

      toast.success(`+${challenge.challenge.xpReward} XP claimed!`);
      fetchChallenges();
      return challenge.challenge.xpReward;
    } catch (error) {
      console.error('Error claiming XP:', error);
      return 0;
    }
  }, [user, dailyChallenges, weeklyChallenges, fetchChallenges]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  return {
    dailyChallenges,
    weeklyChallenges,
    loading,
    updateProgress,
    claimXP,
    refreshChallenges: fetchChallenges,
  };
};
